import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  apiService,
  type Document as ApiDocument,
  type Conversation as ApiConversation,
  type SavedMessage,
  type PDFSnippet,
} from '../services/api';
import { createPDFViewerData, createSnippetViewerData } from './DocumentViewer';
import { EventNames, useEventListener } from '../utils/eventBus';
import type { Message } from '../types/messages';
import type { ViewerData } from '../types/viewer';
import type { MainView } from '../hooks/useNavigationStack';
import logger from '../utils/simpleLogger';
import AppHeader from './AppHeader';
import ChatPanel from './ChatPanel';
import ChatInputArea from './ChatInputArea';
import DocumentViewer from './DocumentViewer';
const GraphProView = lazy(() => import('./GraphProView'));
import DrivePanel from './DrivePanel';
import UnifiedSidebar from './UnifiedSidebar';
const ControlPanelOverlay = lazy(() => import('./control/ControlPanelOverlay'));
import MobileBottomNav from './MobileBottomNav';
import type { MobileView } from './MobileBottomNav';
import type { DocumentSuggestion } from './chat/hooks/useDocumentSuggestions';
import type { DocumentPanelTab } from './chat/document-panel/types';
import ErrorBoundary from './ErrorBoundary';
import UserProfile from './UserProfile';
import ChangePassword from './ChangePassword';
import { useChatSettings } from './chat/hooks/useChatSettings';
import { useMessageConfig } from './chat/hooks/useMessageConfig';
import { NotificationToast } from './NotificationToast';
import { useDocumentSelectionSync } from './useDocumentSelectionSync';
import { useDocumentUploadHandlers } from './useDocumentUploadHandlers';

export interface MainLayoutProps {
  // Auth
  currentUser: any;
  onLogout: () => void;
  onOpenProfile: () => void;
  onOpenChangePassword: () => void;
  onCloseProfile: () => void;
  onCloseChangePassword: () => void;
  showProfileModal: boolean;
  showChangePasswordModal: boolean;
  onProfileUpdate: (updatedUser: any) => Promise<void>;

  // Navigation
  mainView: MainView;
  navigateToView: (
    view: MainView,
    data?: ViewerData | Record<string, unknown>,
    label?: string
  ) => void;
  handleNavigateBack: () => void;
  navigation: any;

  // Panels
  isDriveOpen: boolean;
  handleOpenDrive: () => void;
  setIsDriveOpen: (value: boolean) => void;

  // Viewer
  activeViewer: ViewerData | null;
  setActiveViewer: React.Dispatch<React.SetStateAction<ViewerData | null>>;
  openInViewer: (options: any) => void;
  handleStudioResultView: (studioResult: any) => void;

  // Conversations
  conversations: ApiConversation[];
  currentConversation: ApiConversation | null;
  onConversationCreate?: (name: string) => Promise<any>;
  onSelectConversation?: (conversationId: string) => void;
  onNewConversation?: () => void;
  onUpdateConversation?: (
    id: string,
    updates: { name?: string; is_pinned?: boolean; is_active?: boolean }
  ) => Promise<boolean>;
  onDeleteConversation?: (id: string) => Promise<boolean>;
  onDuplicateConversation?: (id: string) => Promise<any>;

  // Documents
  documents: ApiDocument[];
  selectedDocIds: string[];
  setSelectedDocuments: (ids: string[]) => void;
  loadDocuments: () => Promise<void>;
  handleDocumentSelect: (documentIds: string | string[]) => void;
  handleDocumentDelete: (documentId: string) => Promise<void>;
  handleDocumentReprocess: (documentId: string) => Promise<void>;

  // Messages
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  handleConversationCleared: () => Promise<void>;
  handleSavedMessageView: (savedMessage: SavedMessage) => void;

  // App State
  appState: {
    error: string | null;
    successMessage: string | null;
    clearError: () => void;
    clearSuccessMessage: () => void;
    setSuccessMessage: (message: string) => void;
    setError: (error: string | null) => void;
  };

  // UI State
  savedSnippetId: string | null;
  setSavedSnippetId: (id: string | null) => void;
}

/**
 * MainLayout - Main application layout component.
 *
 * Composes: header, sidebar, chat, viewer, drive, notifications, modals.
 * Document selection sync and upload handlers are delegated to dedicated hooks.
 */
export function MainLayout({
  currentUser,
  onLogout,
  onOpenProfile,
  onOpenChangePassword,
  onCloseProfile,
  onCloseChangePassword,
  showProfileModal,
  showChangePasswordModal,
  onProfileUpdate,
  mainView,
  navigateToView,
  handleNavigateBack,
  navigation,
  isDriveOpen,
  handleOpenDrive,
  setIsDriveOpen,
  activeViewer,
  setActiveViewer,
  openInViewer,
  handleStudioResultView,
  conversations = [],
  currentConversation,
  onConversationCreate,
  onSelectConversation,
  onNewConversation,
  onUpdateConversation,
  onDeleteConversation,
  onDuplicateConversation,
  documents,
  selectedDocIds,
  setSelectedDocuments,
  loadDocuments,
  handleDocumentSelect,
  handleDocumentDelete,
  handleDocumentReprocess,
  messages,
  setMessages,
  handleConversationCleared,
  handleSavedMessageView,
  appState,
  savedSnippetId,
  setSavedSnippetId,
}: MainLayoutProps) {
  const { theme } = useTheme();
  const [chatSuccessMessage, setChatSuccessMessage] = React.useState<string | null>(null);

  // === Document selection sync (context <-> management) ===
  const {
    effectiveSelectedIds,
    selectedMinimalDocuments,
    availableMinimalDocuments,
    contextSelectedIds,
    contextSources,
    contextSelectDocuments,
    contextDeselectDocuments,
  } = useDocumentSelectionSync({ selectedDocIds, setSelectedDocuments, documents });

  // === Shared upload handlers (eliminates duplication between sidebar and ChatInputArea) ===
  const { handleUploadPrivate, handleUploadShared } = useDocumentUploadHandlers({
    loadDocuments,
    appState,
  });

  // === Left sidebar state (unified sidebar + control panel) ===
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );
  const [showControlPanel, setShowControlPanel] = useState(false);
  const [sidebarExpandSection, setSidebarExpandSection] = useState<string | null>(null);
  const [sidebarActiveTab, setSidebarActiveTab] = useState<'chats' | 'documentos'>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
      ? 'chats'
      : 'documentos'
  );
  const [isSidebarPeek, setIsSidebarPeek] = useState(false);

  // === Mobile fullscreen view state ===
  const [mobileView, setMobileView] = useState<MobileView>('chat');
  const isMobile = useCallback(() => window.matchMedia('(max-width: 767px)').matches, []);

  // Suggestions state -- bridged from ChatInputArea via EventBus
  const [panelSuggestions, setPanelSuggestions] = useState<DocumentSuggestion[]>([]);
  const [panelSuggestionsLoading, setPanelSuggestionsLoading] = useState(false);
  const [panelUserPromptNeeded, setPanelUserPromptNeeded] = useState(false);

  useEventListener(
    EventNames.DOCUMENT_SUGGESTIONS_UPDATED,
    useCallback((data: any) => {
      setPanelSuggestions(data?.suggestions ?? []);
      setPanelSuggestionsLoading(data?.isLoading ?? false);
      setPanelUserPromptNeeded(data?.userPromptNeeded ?? false);
    }, [])
  );

  // === Lifted hooks (from ChatPanel to MainLayout for sidebar props) ===
  const chatSettings = useChatSettings();
  const messageConfig = useMessageConfig({
    messages: messages as any,
    selectedDocuments: selectedDocIds,
    conversationId: currentConversation?.id,
    isTyping: false,
    enabled: mainView === 'chat',
    refreshTrigger: chatSettings.configRefreshTrigger,
  });

  // === Shared handler for context-aware document selection ===
  const handleDocumentSelectWithContext = useCallback(
    (ids: string[]) => {
      const toAdd = ids.filter((id) => !contextSelectedIds.includes(id));
      const toRemove = contextSelectedIds.filter((id) => !ids.includes(id));
      if (toAdd.length > 0) contextSelectDocuments(toAdd, 'manual');
      if (toRemove.length > 0) contextDeselectDocuments(toRemove);
      handleDocumentSelect(ids);
    },
    [contextSelectedIds, contextSelectDocuments, contextDeselectDocuments, handleDocumentSelect]
  );

  // === Sidebar toggle helper (shared between event listeners and keyboard shortcut) ===
  const toggleSidebar = useCallback(() => {
    setIsSidebarPeek(false);
    if (isMobile()) {
      setMobileView((prev) => (prev === 'chats' ? 'chat' : 'chats'));
    } else {
      setIsSidebarOpen((prev) => {
        if (!prev) setShowControlPanel(false);
        return !prev;
      });
    }
  }, [isMobile]);

  // === Event listeners for unified sidebar + control panel ===
  useEventListener(EventNames.TOGGLE_SIDEBAR, toggleSidebar);
  useEventListener(EventNames.TOGGLE_CONVERSATION_DRAWER, toggleSidebar);

  useEventListener(
    EventNames.OPEN_DOCUMENT_PANEL,
    (data: { tab?: DocumentPanelTab; onlyIfOpen?: boolean }) => {
      if (data?.onlyIfOpen) {
        if (isMobile() || !isSidebarOpen) return;
        if (data?.tab) setSidebarExpandSection(data.tab);
        return;
      }
      setIsSidebarPeek(false);
      if (isMobile()) {
        setMobileView('docs');
      } else {
        setIsSidebarOpen(true);
        setShowControlPanel(false);
      }
      if (data?.tab) setSidebarExpandSection(data.tab);
    }
  );

  useEventListener(EventNames.TOGGLE_CONTROL_PANEL, () => {
    setIsSidebarPeek(false);
    if (isMobile()) {
      setMobileView((prev) => (prev === 'more' ? 'chat' : 'more'));
    } else {
      setShowControlPanel((prev) => {
        if (!prev) setIsSidebarOpen(false);
        return !prev;
      });
    }
  });

  useEventListener(
    EventNames.CONVERSATION_AUTO_TITLED,
    useCallback(
      (data: { conversationId: string; name: string }) => {
        if (data?.conversationId && data?.name) {
          onUpdateConversation?.(data.conversationId, { name: data.name });
        }
      },
      [onUpdateConversation]
    )
  );

  useEventListener(EventNames.CLOSE_LEFT_PANELS, (data: { except?: string }) => {
    setIsSidebarPeek(false);
    if (data?.except !== 'sidebar') setIsSidebarOpen(false);
    if (data?.except !== 'control') setShowControlPanel(false);
  });

  // Keyboard shortcut: Cmd+\ (Mac) / Ctrl+\ (Windows) to toggle sidebar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        toggleSidebar();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: '100dvh' }}>
      {/* Beta banner */}
      <div className="w-full bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800/50 py-1.5 text-center flex-shrink-0">
        <p className="text-xs text-amber-800 dark:text-amber-200">
          {'\u{1F6A7}'} Aplicaci&oacute;n en per&iacute;odo de prueba. Acceso limitado a usuarios
          invitados.
        </p>
      </div>

      {/* Header */}
      <AppHeader
        currentUser={currentUser}
        onLogout={onLogout}
        onOpenProfile={onOpenProfile}
        isChatActive={mainView === 'chat'}
        onClose={mainView !== 'chat' ? handleNavigateBack : undefined}
      />

      {/* Main content area */}
      <main className="flex-1 overflow-hidden relative flex flex-col">
        {mainView === 'chat' && (
          <ChatView
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            isSidebarPeek={isSidebarPeek}
            setIsSidebarPeek={setIsSidebarPeek}
            isMobile={isMobile}
            mobileView={mobileView}
            setMobileView={setMobileView}
            showControlPanel={showControlPanel}
            setShowControlPanel={setShowControlPanel}
            sidebarExpandSection={sidebarExpandSection}
            setSidebarExpandSection={setSidebarExpandSection}
            setSidebarActiveTab={setSidebarActiveTab}
            conversations={conversations}
            currentConversation={currentConversation}
            onConversationCreate={onConversationCreate}
            onSelectConversation={onSelectConversation}
            onNewConversation={onNewConversation}
            onUpdateConversation={onUpdateConversation}
            effectiveSelectedIds={effectiveSelectedIds}
            selectedMinimalDocuments={selectedMinimalDocuments}
            availableMinimalDocuments={availableMinimalDocuments}
            contextSelectedIds={contextSelectedIds}
            contextSources={contextSources}
            contextSelectDocuments={contextSelectDocuments}
            contextDeselectDocuments={contextDeselectDocuments}
            handleDocumentSelect={handleDocumentSelect}
            handleDocumentSelectWithContext={handleDocumentSelectWithContext}
            panelSuggestions={panelSuggestions}
            panelSuggestionsLoading={panelSuggestionsLoading}
            panelUserPromptNeeded={panelUserPromptNeeded}
            chatSettings={chatSettings}
            messageConfig={messageConfig}
            messages={messages}
            setMessages={setMessages}
            handleConversationCleared={handleConversationCleared}
            navigateToView={navigateToView}
            setActiveViewer={setActiveViewer}
            handleStudioResultView={handleStudioResultView}
            chatSuccessMessage={chatSuccessMessage}
            setChatSuccessMessage={setChatSuccessMessage}
            handleUploadPrivate={handleUploadPrivate}
            handleUploadShared={currentUser?.is_admin ? handleUploadShared : undefined}
            currentUser={currentUser}
            loadDocuments={loadDocuments}
            appState={appState}
          />
        )}

        {mainView === 'viewer' && (
          <DocumentViewer
            viewerData={activeViewer}
            onClose={handleNavigateBack}
            onCitationClick={(citationData) => {
              const pdfData = createPDFViewerData(citationData);
              setActiveViewer(pdfData);
              navigation.replace({
                view: 'viewer',
                data: pdfData,
                label: `PDF: ${citationData.document_name}`,
              });
            }}
            onChunkClick={(citationData) => {
              const pdfData = createPDFViewerData(citationData);
              setActiveViewer(pdfData);
              navigation.replace({
                view: 'viewer',
                data: pdfData,
                label: `Chunk: ${citationData.document_name}`,
              });
            }}
            onSnippetSaved={(snippetId) => {
              setSavedSnippetId(snippetId);
              appState.setSuccessMessage('Fragmento guardado autom\u00e1ticamente');
              setTimeout(() => {
                appState.clearSuccessMessage();
                setSavedSnippetId(null);
              }, 5000);
            }}
          />
        )}

        {mainView === 'graph-pro' && (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            }
          >
            <GraphProView
              onElementClick={(elementId, elementType) => {
                logger.info('Element clicked for analysis:', elementId, elementType);
              }}
              onClose={handleNavigateBack}
            />
          </Suspense>
        )}

        {mainView === 'drive' && (
          <DriveView
            documents={documents}
            loadDocuments={loadDocuments}
            openInViewer={openInViewer}
            handleDocumentDelete={handleDocumentDelete}
            handleDocumentReprocess={handleDocumentReprocess}
            setActiveViewer={setActiveViewer}
            navigateToView={navigateToView}
            handleSavedMessageView={handleSavedMessageView}
            handleStudioResultView={handleStudioResultView}
            handleNavigateBack={handleNavigateBack}
          />
        )}
      </main>

      {/* Notification Toasts */}
      <NotificationToast
        message={appState.error}
        type="error"
        onDismiss={() => appState.clearError()}
      />
      {!appState.error && (
        <NotificationToast
          message={appState.successMessage}
          type="success"
          onDismiss={() => {
            appState.clearSuccessMessage();
            setSavedSnippetId(null);
          }}
        >
          {savedSnippetId && (
            <button
              onClick={() => {
                appState.clearSuccessMessage();
                setSavedSnippetId(null);
              }}
              className="mt-2 text-xs text-green-300 hover:text-green-200 underline"
            >
              Editar
            </button>
          )}
        </NotificationToast>
      )}

      {/* Modals */}
      {showProfileModal && currentUser && (
        <UserProfile user={currentUser} onClose={onCloseProfile} onUpdate={onProfileUpdate} />
      )}
      {showChangePasswordModal && <ChangePassword onClose={onCloseChangePassword} />}
    </div>
  );
}

// ============================================================
// Sub-components extracted for readability
// ============================================================

/**
 * ChatView - The chat main view with sidebar, control panel, chat panel, and input area.
 * Extracted from MainLayout to reduce its JSX nesting depth.
 */
function ChatView({
  isSidebarOpen,
  setIsSidebarOpen,
  isSidebarPeek,
  setIsSidebarPeek,
  isMobile,
  mobileView,
  setMobileView,
  showControlPanel,
  setShowControlPanel,
  sidebarExpandSection,
  setSidebarExpandSection,
  setSidebarActiveTab,
  conversations,
  currentConversation,
  onConversationCreate,
  onSelectConversation,
  onNewConversation,
  onUpdateConversation,
  effectiveSelectedIds,
  selectedMinimalDocuments,
  availableMinimalDocuments,
  contextSelectedIds,
  contextSources,
  contextSelectDocuments,
  contextDeselectDocuments,
  handleDocumentSelect,
  handleDocumentSelectWithContext,
  panelSuggestions,
  panelSuggestionsLoading,
  panelUserPromptNeeded,
  chatSettings,
  messageConfig,
  messages,
  setMessages,
  handleConversationCleared,
  navigateToView,
  setActiveViewer,
  handleStudioResultView,
  chatSuccessMessage,
  setChatSuccessMessage,
  handleUploadPrivate,
  handleUploadShared,
  currentUser,
  loadDocuments,
  appState,
}: any) {
  return (
    <div className="flex-1 flex flex-row overflow-hidden">
      {/* Unified Sidebar (Chat + Docs tabs) */}
      <UnifiedSidebar
        isOpen={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setIsSidebarPeek(false);
          if (isMobile()) setMobileView('chat');
        }}
        onMouseLeave={() => {
          if (isSidebarPeek) {
            setIsSidebarOpen(false);
            setIsSidebarPeek(false);
          }
        }}
        onInteraction={() => {
          if (isSidebarPeek) setIsSidebarPeek(false);
        }}
        mobileFullscreen={mobileView === 'chats' || mobileView === 'docs'}
        expandSection={sidebarExpandSection}
        onExpandSectionHandled={() => setSidebarExpandSection(null)}
        onTabChange={(tab: any) => setSidebarActiveTab(tab)}
        onToggleDrive={() => navigateToView('drive', undefined, 'Documentos')}
        conversations={conversations}
        currentConversation={currentConversation}
        onConversationSelect={(conv: any) => {
          onSelectConversation?.(conv.id);
          if (isMobile()) {
            setMobileView('chat');
            setIsSidebarOpen(false);
          }
        }}
        onConversationCreate={async (name: string) => onConversationCreate?.(name)}
        onConversationUpdate={async (id: string, updates: any) =>
          onUpdateConversation?.(id, updates) ?? false
        }
        selectedDocumentIds={effectiveSelectedIds}
        selectedDocuments={selectedMinimalDocuments}
        sources={contextSources}
        onDocumentsSelect={handleDocumentSelectWithContext}
        suggestions={panelSuggestions}
        isSuggestionsLoading={panelSuggestionsLoading}
        userPromptNeeded={panelUserPromptNeeded}
        documentSelectionMode={chatSettings.documentSelectionMode}
        onUploadPrivateFile={handleUploadPrivate}
      />

      {/* Control Panel / Configuracion */}
      <Suspense fallback={null}>
        <ControlPanelOverlay
          isOpen={showControlPanel}
          onClose={() => {
            setShowControlPanel(false);
            if (isMobile()) setMobileView('chat');
          }}
          mobileFullscreen={mobileView === 'more'}
          configInfo={messageConfig.configInfo}
          isLoading={messageConfig.loadingConfigInfo['']}
          onModelChange={chatSettings.handleModelChange}
          onTemperatureChange={chatSettings.handleTemperatureChange}
          onDocumentsChange={handleDocumentSelectWithContext}
          suggestedQuestionsEnabled={chatSettings.suggestedQuestionsEnabled}
          suggestedTasksEnabled={chatSettings.suggestedTasksEnabled}
          onSuggestedQuestionsToggle={chatSettings.handleSuggestedQuestionsToggle}
          onSuggestedTasksToggle={chatSettings.handleSuggestedTasksToggle}
        />
      </Suspense>

      {/* Chat column -- hidden on mobile when viewing other tabs */}
      <div
        className={`flex-1 flex flex-col overflow-hidden min-w-0 max-md:pb-[var(--mobile-nav-total)] ${
          mobileView !== 'chat' ? 'max-md:hidden' : ''
        }`}
      >
        <div className="flex-1 overflow-hidden">
          <ChatPanel
            conversation={currentConversation}
            selectedDocuments={effectiveSelectedIds}
            selectedMinimalDocuments={selectedMinimalDocuments}
            availableDocuments={availableMinimalDocuments}
            onDocumentSelect={(docIds: string[]) => {
              const currentIds = contextSelectedIds;
              const toAdd = docIds.filter((id: string) => !currentIds.includes(id));
              const toRemove = currentIds.filter((id: string) => !docIds.includes(id));
              if (toAdd.length > 0) contextSelectDocuments(toAdd, 'manual');
              if (toRemove.length > 0) contextDeselectDocuments(toRemove);
              handleDocumentSelect(docIds);
            }}
            messages={messages as any}
            onMessagesUpdate={setMessages as any}
            onCitationClick={(citationData: any) => {
              const pdfData = createPDFViewerData(citationData);
              setActiveViewer(pdfData);
              navigateToView(
                'viewer',
                pdfData,
                `PDF: ${citationData.document_name || 'Documento'}`
              );
            }}
            onChunkClick={(citationData: any) => {
              const pdfData = createPDFViewerData(citationData);
              setActiveViewer(pdfData);
              navigateToView(
                'viewer',
                pdfData,
                `Chunk: ${citationData.document_name || 'Documento'}`
              );
            }}
            onStudioResultOpen={handleStudioResultView}
            onConversationCleared={handleConversationCleared}
            successMessage={chatSuccessMessage}
            onDismissSuccess={() => setChatSuccessMessage(null)}
            suggestedQuestionsEnabled={chatSettings.suggestedQuestionsEnabled}
            suggestedTasksEnabled={chatSettings.suggestedTasksEnabled}
            configRefreshTrigger={chatSettings.configRefreshTrigger}
            configInfo={messageConfig.configInfo}
            historicalConfigInfo={messageConfig.historicalConfigInfo}
            loadingConfigInfo={messageConfig.loadingConfigInfo}
            onModelChange={chatSettings.handleModelChange}
            onTemperatureChange={chatSettings.handleTemperatureChange}
            onSuggestedQuestionsToggle={chatSettings.handleSuggestedQuestionsToggle}
            onSuggestedTasksToggle={chatSettings.handleSuggestedTasksToggle}
            onConversationRename={
              currentConversation?.id
                ? async (name: string) => {
                    await onUpdateConversation?.(currentConversation.id, { name });
                  }
                : undefined
            }
            onConversationPin={
              currentConversation?.id
                ? async (pinned: boolean) => {
                    await onUpdateConversation?.(currentConversation.id, { is_pinned: pinned });
                  }
                : undefined
            }
            onConversationArchive={
              currentConversation?.id
                ? async () => {
                    await onUpdateConversation?.(currentConversation.id, { is_active: false });
                  }
                : undefined
            }
            onConversationCopy={() => {
              if (!messages || messages.length === 0) return;
              const text = messages
                .map((m: any) => `${m.isUser ? 'T\u00fa' : 'Asistente'}: ${m.text}`)
                .join('\n\n');
              navigator.clipboard.writeText(text).catch(() => {});
            }}
            isSidebarOpen={isSidebarOpen}
            onToggleSidebar={() => {
              if (!isMobile()) {
                setIsSidebarPeek(false);
                setIsSidebarOpen((prev: boolean) => {
                  if (!prev) setShowControlPanel(false);
                  return !prev;
                });
              }
            }}
            onNewConversation={onNewConversation}
          />
        </div>

        {/* Chat Input Area */}
        <div className="flex-shrink-0 overflow-visible w-full">
          <ChatInputArea
            conversation={currentConversation}
            onConversationCreate={onConversationCreate}
            onNewConversation={onNewConversation}
            selectedDocuments={effectiveSelectedIds}
            selectedMinimalDocuments={selectedMinimalDocuments}
            allDocumentIds={availableMinimalDocuments.map((d: any) => d.id)}
            onDocumentSelect={handleDocumentSelect as any}
            messages={messages as any}
            onMessagesUpdate={setMessages as any}
            onConversationCleared={handleConversationCleared}
            onDocumentUpload={handleUploadPrivate}
            onDocumentUploadShared={handleUploadShared}
            currentUser={currentUser || undefined}
            onSuccessMessageChange={setChatSuccessMessage}
          />
        </div>
      </div>

      {/* Mobile bottom tab bar */}
      <MobileBottomNav
        mobileView={mobileView}
        selectedDocsCount={effectiveSelectedIds.length}
        onTabPress={(tab: MobileView) => {
          setMobileView(tab);
          setIsSidebarPeek(false);
          if (tab === 'chats') {
            setIsSidebarOpen(true);
            setShowControlPanel(false);
            setSidebarExpandSection('chats');
          } else if (tab === 'docs') {
            setIsSidebarOpen(true);
            setShowControlPanel(false);
            setSidebarExpandSection('seleccionados');
          } else if (tab === 'more') {
            setIsSidebarOpen(false);
            setShowControlPanel(true);
          } else {
            setIsSidebarOpen(false);
            setShowControlPanel(false);
          }
        }}
      />
    </div>
  );
}

/**
 * DriveView - The drive panel as a main view.
 * Extracted from MainLayout to reduce JSX nesting depth.
 */
function DriveView({
  documents,
  loadDocuments,
  openInViewer,
  handleDocumentDelete,
  handleDocumentReprocess,
  setActiveViewer,
  navigateToView,
  handleSavedMessageView,
  handleStudioResultView,
  handleNavigateBack,
}: any) {
  return (
    <ErrorBoundary>
      <DrivePanel
        documents={documents}
        onDocumentUpload={async (document: any) => {
          if (document) await loadDocuments();
        }}
        onDocumentView={(document: any) => {
          if (!document?.original_filename) {
            console.warn('Documento sin filename original');
            return;
          }
          openInViewer({
            filename: document.original_filename,
            displayName: document.name,
            documentId: document.id,
            page: 1,
            scope: document.scope,
            view_url: document.view_url,
          });
        }}
        onDocumentDelete={async (document: ApiDocument) => {
          await handleDocumentDelete(document.id);
        }}
        onDocumentReprocess={(documentId: string) => handleDocumentReprocess(documentId)}
        onSnippetClick={async (snippet: any) => {
          try {
            let fullSnippet: PDFSnippet = snippet as PDFSnippet;
            if (!snippet.image_data && snippet.has_image) {
              try {
                fullSnippet = await apiService.getPDFSnippet(snippet.id);
              } catch (error) {
                console.warn('No se pudo cargar imagen del snippet:', error);
              }
            }
            const snippetData = createSnippetViewerData(
              fullSnippet.text_content || '',
              fullSnippet
            );
            setActiveViewer(snippetData);
            navigateToView(
              'viewer',
              snippetData,
              `Snippet: ${fullSnippet.title || 'Sin t\u00edtulo'}`
            );
          } catch (error) {
            console.error('Error abriendo snippet:', error);
          }
        }}
        onSavedMessageView={handleSavedMessageView}
        onStudioResultView={handleStudioResultView}
        isCollapsed={false}
        onToggleCollapse={() => {}}
        onResetWidth={() => {}}
        panelPosition="left"
        onPanelChange={() => {}}
        availablePanels={['drive']}
        currentPanel="drive"
        onClose={handleNavigateBack}
      />
    </ErrorBoundary>
  );
}
